import React, { useEffect, useMemo, useState } from "react";
import { Search, Bell, Package, X, ImageOff, Bookmark } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const idr = (n) => `Rp. ${(Number(n) || 0).toLocaleString("id-ID")}`;
const STATUS = {
  Aman: "bg-blue-500 text-white",
  Ready: "bg-amber-400 text-amber-950",
  Habis: "bg-rose-500 text-white",
};
const photoSrc = (mp) => (!mp ? "" : (/^https?:\/\//.test(mp) ? mp : `${process.env.REACT_APP_BACKEND_URL}/api/files/${mp}`));

export default function PublicCatalog() {
  const [meta, setMeta] = useState({ app_name: "Ventedaily", logo_url: "" });
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/public/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  const params = useMemo(() => ({ q: debounced || undefined, page, limit }), [debounced, page, limit]);
  useEffect(() => { setLoading(true); api.get("/public/catalog", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [params]);

  const from = data.total === 0 ? 0 : (data.page - 1) * limit + 1;
  const to = Math.min(data.page * limit, data.total);
  const pages = data.pages || 1;
  const win = [];
  { const s = Math.max(1, data.page - 1), e = Math.min(pages, data.page + 1); if (s > 1) win.push(1); if (s > 2) win.push("..."); for (let i = s; i <= e; i++) win.push(i); if (e < pages - 1) win.push("..."); if (e < pages) win.push(pages); }

  return (
    <div className="min-h-screen bg-[#eef1f6]" data-testid="public-catalog-page">
      <header className="bg-gradient-to-r from-[#e6e9f5] to-[#eef1f6] border-b border-[#dfe3ee] sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center gap-2">
          <Bell className="w-5 h-5 text-indigo-500" />
          <span className="font-heading font-bold text-[#3a3f52] tracking-wide">{meta.app_name || "JIK NEW"}</span>
          <span className="text-indigo-400">·</span>
          <Link to="/cek-keep" data-testid="public-check-keep-btn" className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"><Bookmark className="w-4 h-4" /> Check Keep</Link>
        </div>
        {meta.marquee ? (
          <div className="bg-indigo-600 text-white overflow-hidden" data-testid="public-marquee">
            <style>{`@keyframes vk_marquee{0%{transform:translateX(100%)}100%{transform:translateX(-100%)}}`}</style>
            <div className="whitespace-nowrap py-1.5 text-sm font-medium" style={{ animation: "vk_marquee 22s linear infinite" }}>{meta.marquee}</div>
          </div>
        ) : null}
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-[#e7eaf2] p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h1 className="text-xl font-heading font-semibold text-[#3a3f52]">Daftar Product</h1>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." data-testid="public-catalog-search"
                className="pl-9 pr-3 py-2 rounded-lg bg-[#f4f6fa] border border-[#e2e6ef] text-sm outline-none focus:border-indigo-300 w-64" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#8b91a3] border-b border-[#eceef4]">
                  <th className="py-2.5 px-3 w-16 font-semibold text-center">No</th>
                  <th className="py-2.5 px-3 font-semibold">Nama</th>
                  <th className="py-2.5 px-3 font-semibold text-center">Stock</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Harga Jual</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={it.id} onClick={() => setDetail(it.product_id)} data-testid={`public-catalog-row-${it.id}`}
                    className="border-b border-[#f2f4f8] hover:bg-[#f7f9fc] cursor-pointer transition-colors">
                    <td className="py-2.5 px-3 text-center font-semibold text-[#3a3f52]">{from + i}</td>
                    <td className="py-2.5 px-3 text-[#4b5063]">{it.name}</td>
                    <td className="py-2.5 px-3 text-center"><span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${STATUS[it.status] || "bg-slate-300 text-slate-700"}`}>{it.status}</span></td>
                    <td className="py-2.5 px-3 text-right text-[#4b5063]">{idr(it.selling_price)}</td>
                  </tr>
                ))}
                {!loading && !data.items.length && <tr><td colSpan={4} className="text-center text-slate-400 py-12"><Package className="w-6 h-6 mx-auto mb-2 opacity-50" />Produk tidak ditemukan.</td></tr>}
                {loading && <tr><td colSpan={4} className="text-center text-slate-400 py-12">Memuat…</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 mt-4 text-sm text-[#6b7180]">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }} data-testid="public-catalog-pagesize"
                className="border border-[#e2e6ef] rounded-md px-2 py-1 bg-white outline-none">
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>entries</span>
              <span className="ml-2 text-[#8b91a3]">Showing {from} to {to} of {data.total} entries</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="public-catalog-prev"
                className="px-3 py-1.5 rounded-md border border-[#e2e6ef] bg-white disabled:opacity-40 hover:bg-slate-50">Previous</button>
              {win.map((p, idx) => p === "..." ? <span key={idx} className="px-2 text-slate-400">…</span> : (
                <button key={idx} onClick={() => setPage(p)} data-testid={`public-catalog-page-${p}`}
                  className={`px-3 py-1.5 rounded-md border ${p === data.page ? "bg-indigo-600 text-white border-indigo-600" : "border-[#e2e6ef] bg-white hover:bg-slate-50"}`}>{p}</button>
              ))}
              <button disabled={data.page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="public-catalog-next"
                className="px-3 py-1.5 rounded-md border border-[#e2e6ef] bg-white disabled:opacity-40 hover:bg-slate-50">Next</button>
            </div>
          </div>
        </div>
      </main>

      {detail && <ProductDetail pid={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ProductDetail({ pid, onClose }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  useEffect(() => { api.get(`/public/product/${pid}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [pid]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="public-product-detail">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 sticky top-0 bg-white">
          <h2 className="font-heading font-semibold text-[#3a3f52]">{p ? p.name : "Memuat…"}</h2>
          <button onClick={onClose} data-testid="public-detail-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {err ? <div className="p-8 text-center text-slate-400">Produk tidak ditemukan.</div> : !p ? <div className="p-8 text-center text-slate-400">Memuat…</div> : (
          <div className="p-5 space-y-4">
            <div className="flex gap-4 flex-col sm:flex-row">
              <div className="w-40 h-40 shrink-0 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
                {p.main_photo && !imgErr ? <img src={photoSrc(p.main_photo)} alt={p.name} onError={() => setImgErr(true)} className="w-full h-full object-cover" data-testid="public-detail-photo" /> : <div className="text-slate-400 flex flex-col items-center text-xs"><ImageOff className="w-8 h-8 mb-1 opacity-50" />Tanpa foto</div>}
              </div>
              <div className="flex-1">
                {p.category && <div className="text-xs text-slate-400 mb-1">{p.category}</div>}
                <div className="text-lg font-heading font-semibold text-[#3a3f52]">{p.name}</div>
                <div className="text-sm text-slate-500 mt-1">{p.variants.length} varian</div>
              </div>
            </div>
            <div className="border border-slate-100 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 bg-slate-50"><th className="py-2 px-3 font-semibold">Warna</th><th className="py-2 px-3 font-semibold">Size</th><th className="py-2 px-3 font-semibold text-center">Stock</th><th className="py-2 px-3 font-semibold text-right">Harga Jual</th></tr></thead>
                <tbody>
                  {p.variants.map((v) => (
                    <tr key={v.id} className="border-t border-slate-100" data-testid={`public-detail-variant-${v.id}`}>
                      <td className="py-2 px-3 text-[#4b5063]">{v.color || "-"}</td>
                      <td className="py-2 px-3 text-[#4b5063]">{v.size || "-"}</td>
                      <td className="py-2 px-3 text-center"><span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${STATUS[v.status] || "bg-slate-300 text-slate-700"}`}>{v.status}</span></td>
                      <td className="py-2 px-3 text-right text-[#4b5063]">{idr(v.selling_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
